import React, { useState, useEffect } from "react";
import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp,
    query,
    orderBy
} from "firebase/firestore";
import { db } from "../firebase";
import Loader from "../components/Loader";

const COLLECTION_NAME = "LOCATION_MASTER";

const LocationMaster = ({ onUpdate }) => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ name: "" });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, COLLECTION_NAME), orderBy("name"));
            const snap = await getDocs(q);
            setLocations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Error fetching locations:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return alert("Location name is required");

        setLoading(true);
        try {
            const data = { name: formData.name.trim().toUpperCase() };

            if (editingId) {
                await updateDoc(doc(db, COLLECTION_NAME, editingId), data);
            } else {
                await addDoc(collection(db, COLLECTION_NAME), {
                    ...data,
                    createdAt: Timestamp.now()
                });
            }
            setShowModal(false);
            setFormData({ name: "" });
            setEditingId(null);
            fetchLocations();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error("Error saving location:", err);
            alert("Failed to save location");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (loc) => {
        setFormData({ name: loc.name });
        setEditingId(loc.id);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this location? Parts assigned to this location will not be affected but the location will no longer appear in dropdowns.")) return;
        setLoading(true);
        try {
            await deleteDoc(doc(db, COLLECTION_NAME, id));
            fetchLocations();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error("Error deleting location:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 text-primary">Manage Part Locations</h5>
                <button className="btn btn-sm btn-primary" onClick={() => { setEditingId(null); setFormData({ name: "" }); setShowModal(true); }}>
                    + Add New Location
                </button>
            </div>

            {loading ? <Loader /> : (
                <div className="card shadow-sm border-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Location Name</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {locations.map(loc => (
                                    <tr key={loc.id}>
                                        <td className="fw-bold">{loc.name}</td>
                                        <td className="text-end">
                                            <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(loc)}>✏</button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(loc.id)}>🗑</button>
                                        </td>
                                    </tr>
                                ))}
                                {locations.length === 0 && (
                                    <tr><td colSpan="2" className="text-center py-4">No locations found. Add one to get started.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content shadow-lg border-0">
                            <form onSubmit={handleSubmit}>
                                <div className="modal-header bg-light">
                                    <h5 className="modal-title">{editingId ? "Edit Location" : "Add Location"}</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="mb-3">
                                        <label className="fw-bold small mb-1">Location Code / Name</label>
                                        <input
                                            className="form-control"
                                            required
                                            placeholder="e.g. GRPD"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer bg-light">
                                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : (editingId ? "Update Location" : "Save Location")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationMaster;
